import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Eye, FileText, FolderPlus, X, Save, ArrowLeft, Calendar, Search, Globe, Clock, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import SimpleEditor from '../../components/SimpleEditor';

const BACKEND_URL = '';

/** SEO Preview — mock Google search snippet */
function SeoPreview({ title, description, slug, tenantSlug }) {
  const displayTitle = title || 'Article Title';
  const displayDesc = description || 'Add a meta description to preview how this article will appear in search results...';
  const displayUrl = `edrano.my/app/${tenantSlug || 'your-portal'}/blog/${slug || 'article-slug'}`;

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-4" data-testid="seo-preview">
      <div className="flex items-center gap-1.5 mb-2">
        <Globe className="w-3.5 h-3.5 text-stone-400" />
        <span className="text-xs font-medium text-stone-500 uppercase tracking-wide">SEO Preview</span>
      </div>
      <div className="space-y-0.5">
        <div className="text-sm text-stone-400 truncate">{displayUrl}</div>
        <div className="text-lg text-blue-700 font-medium leading-snug line-clamp-1">{displayTitle}</div>
        <div className="text-sm text-stone-600 line-clamp-2 leading-relaxed">{displayDesc}</div>
      </div>
    </div>
  );
}

/**
 * Admin blog management page.
 * Allows creating/editing articles and managing categories.
 */
export default function BlogManager() {
  const [tenantSlug, setTenantSlug] = useState('');
  const [tenants, setTenants] = useState([]);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | editor
  const [editingArticle, setEditingArticle] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [generatingMeta, setGeneratingMeta] = useState(false);

  // Load tenant slug from user's tenant_id
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (!userStr || !token) return;
    try {
      const user = JSON.parse(userStr);
      const tenantId = user.tenant_id;
      if (!tenantId) return;
      fetch(`${BACKEND_URL}/api/tenant/settings/${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => {
          if (data.slug) {
            setTenantSlug(data.slug);
            setTenants([{ slug: data.slug, name: data.name || data.slug }]);
          }
        })
        .catch(() => {});
    } catch {}
  }, []);

  // Load articles + categories when tenant changes
  const loadData = useCallback(async () => {
    if (!tenantSlug) return;
    setLoading(true);
    try {
      const [artRes, catRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/blog/${tenantSlug}/articles?status=`),
        fetch(`${BACKEND_URL}/api/blog/${tenantSlug}/categories`)
      ]);
      const artData = await artRes.json();
      const catData = await catRes.json();
      setArticles(artData.articles || []);
      setCategories(catData.categories || []);
    } catch (err) {
      toast.error('Failed to load blog data');
    } finally {
      setLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Category Management ───
  const createCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/blog/${tenantSlug}/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Category created');
      setNewCategoryName('');
      setShowCategoryModal(false);
      loadData();
    } catch {
      toast.error('Failed to create category');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/blog/${tenantSlug}/categories/${id}`, { method: 'DELETE' });
      toast.success('Category deleted');
      loadData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // ─── Article Management ───
  const newArticle = () => {
    setEditingArticle({
      title: '', content: '', excerpt: '', featured_image: '',
      category_slug: '', tags: [], meta_description: '', status: 'draft',
      scheduled_at: ''
    });
    setView('editor');
  };

  const editArticle = async (slug) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/blog/${tenantSlug}/articles/${slug}`);
      const data = await res.json();
      setEditingArticle(data.article);
      setView('editor');
    } catch {
      toast.error('Failed to load article');
    }
  };

  const saveArticle = async () => {
    if (!editingArticle.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (editingArticle.status === 'scheduled' && !editingArticle.scheduled_at) {
      toast.error('Please set a scheduled date/time');
      return;
    }
    try {
      const isNew = !editingArticle.id;
      const url = isNew
        ? `${BACKEND_URL}/api/blog/${tenantSlug}/articles`
        : `${BACKEND_URL}/api/blog/${tenantSlug}/articles/${editingArticle.id}`;

      const payload = {
        title: editingArticle.title,
        content: editingArticle.content,
        excerpt: editingArticle.excerpt,
        featured_image: editingArticle.featured_image,
        category_slug: editingArticle.category_slug,
        tags: editingArticle.tags,
        meta_description: editingArticle.meta_description,
        status: editingArticle.status,
        scheduled_at: editingArticle.status === 'scheduled' ? editingArticle.scheduled_at : null
      };

      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Failed');
      toast.success(isNew ? 'Article created' : 'Article updated');
      setView('list');
      setEditingArticle(null);
      loadData();
    } catch {
      toast.error('Failed to save article');
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article permanently?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/blog/${tenantSlug}/articles/${id}`, { method: 'DELETE' });
      toast.success('Article deleted');
      loadData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const updateField = (field, value) => {
    setEditingArticle(prev => ({ ...prev, [field]: value }));
  };

  const generateMeta = async () => {
    if (!editingArticle.title.trim()) {
      toast.error('Enter a title first');
      return;
    }
    setGeneratingMeta(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/blog/${tenantSlug}/generate-meta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingArticle.title,
          excerpt: editingArticle.excerpt || '',
          content: editingArticle.content || ''
        })
      });
      if (res.status === 402) {
        toast.error('No AI credits available. Add credits to use AI features.');
        return;
      }
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      updateField('meta_description', data.meta_description);
      toast.success(`Meta description generated (${data.credits_remaining} credits left)`);
    } catch {
      toast.error('Failed to generate meta description');
    } finally {
      setGeneratingMeta(false);
    }
  };

  // ─── Editor View ───
  if (view === 'editor' && editingArticle) {
    return (
      <div className="max-w-4xl mx-auto" data-testid="blog-editor">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => { setView('list'); setEditingArticle(null); }}
            className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700"
          >
            <ArrowLeft className="w-4 h-4" /> Back to articles
          </button>
          <div className="flex items-center gap-2">
            <select
              value={editingArticle.status}
              onChange={(e) => updateField('status', e.target.value)}
              className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 bg-white"
              data-testid="article-status-select"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="scheduled">Scheduled</option>
            </select>
            <Button onClick={saveArticle} size="sm" className="bg-stone-900 hover:bg-stone-800" data-testid="save-article-btn">
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Title */}
          <Input
            value={editingArticle.title}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder="Article title"
            className="text-xl font-semibold h-12"
            data-testid="article-title-input"
          />

          {/* Excerpt */}
          <Input
            value={editingArticle.excerpt}
            onChange={(e) => updateField('excerpt', e.target.value)}
            placeholder="Short excerpt / description"
            data-testid="article-excerpt-input"
          />

          {/* Featured Image URL */}
          <Input
            value={editingArticle.featured_image}
            onChange={(e) => updateField('featured_image', e.target.value)}
            placeholder="Featured image URL"
            data-testid="article-image-input"
          />
          {editingArticle.featured_image && (
            <div className="rounded-lg overflow-hidden border border-stone-200 h-40">
              <img src={editingArticle.featured_image} alt="" className="w-full h-full object-cover" />
            </div>
          )}

          {/* Category + Tags row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-stone-500 mb-1 block">Category</label>
              <select
                value={editingArticle.category_slug}
                onChange={(e) => updateField('category_slug', e.target.value)}
                className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white"
                data-testid="article-category-select"
              >
                <option value="">No category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">Tags (comma separated)</label>
              <Input
                value={(editingArticle.tags || []).join(', ')}
                onChange={(e) => updateField('tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                placeholder="seo, hosting, wordpress"
                data-testid="article-tags-input"
              />
            </div>
          </div>

          {/* Meta Description */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-stone-500">Meta Description (SEO)</label>
              <button
                onClick={generateMeta}
                disabled={generatingMeta || !editingArticle.title.trim()}
                className="flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="generate-meta-btn"
              >
                <Sparkles className={`w-3.5 h-3.5 ${generatingMeta ? 'animate-spin' : ''}`} />
                {generatingMeta ? 'Generating...' : 'Generate with AI'}
              </button>
            </div>
            <Input
              value={editingArticle.meta_description}
              onChange={(e) => updateField('meta_description', e.target.value)}
              placeholder="SEO meta description (recommended: 150-160 characters)"
              maxLength={160}
              data-testid="article-meta-input"
            />
            <div className="text-xs text-stone-400 mt-1 text-right">
              {(editingArticle.meta_description || '').length}/160
            </div>
          </div>

          {/* Schedule Date/Time — shown only when status is "scheduled" */}
          {editingArticle.status === 'scheduled' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="schedule-picker">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-amber-600" />
                <label className="text-sm font-medium text-amber-800">Schedule Publication</label>
              </div>
              <input
                type="datetime-local"
                value={editingArticle.scheduled_at ? editingArticle.scheduled_at.slice(0, 16) : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  updateField('scheduled_at', val ? new Date(val).toISOString() : '');
                }}
                min={new Date().toISOString().slice(0, 16)}
                className="w-full text-sm border border-amber-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                data-testid="schedule-datetime-input"
              />
              {editingArticle.scheduled_at && (
                <p className="text-xs text-amber-600 mt-1.5">
                  Will publish: {new Date(editingArticle.scheduled_at).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* SEO Preview */}
          <SeoPreview
            title={editingArticle.title}
            description={editingArticle.meta_description || editingArticle.excerpt}
            slug={editingArticle.slug}
            tenantSlug={tenantSlug}
          />

          {/* Content Editor */}
          <div>
            <label className="text-xs text-stone-500 mb-1 block">Content</label>
            <SimpleEditor
              value={editingArticle.content}
              onChange={(html) => updateField('content', html)}
              placeholder="Write your article content here..."
            />
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ───
  return (
    <div data-testid="blog-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-stone-900">Blog & Documentation</h2>
          <p className="text-sm text-stone-500 mt-0.5">Create articles, documentation, and SEO content for your portal</p>
        </div>
        <div className="flex items-center gap-2">
          {tenants.length > 1 && (
            <select
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              className="text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white"
            >
              {tenants.map(t => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </select>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowCategoryModal(true)} data-testid="manage-categories-btn">
            <FolderPlus className="w-4 h-4 mr-1" /> Categories
          </Button>
          <Button size="sm" onClick={newArticle} className="bg-stone-900 hover:bg-stone-800" data-testid="new-article-btn">
            <Plus className="w-4 h-4 mr-1" /> New Article
          </Button>
        </div>
      </div>

      {/* Articles Table */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-lg border border-stone-200 p-4 animate-pulse">
              <div className="h-4 bg-stone-200 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-stone-200" data-testid="no-articles">
          <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h3 className="text-stone-600 font-medium mb-1">No articles yet</h3>
          <p className="text-stone-400 text-sm mb-4">Create your first article to get started</p>
          <Button size="sm" onClick={newArticle} className="bg-stone-900 hover:bg-stone-800">
            <Plus className="w-4 h-4 mr-1" /> Create Article
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="articles-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-100 text-left">
                <th className="px-4 py-3 text-xs font-medium text-stone-500 uppercase">Title</th>
                <th className="px-4 py-3 text-xs font-medium text-stone-500 uppercase hidden sm:table-cell">Category</th>
                <th className="px-4 py-3 text-xs font-medium text-stone-500 uppercase hidden md:table-cell">Status</th>
                <th className="px-4 py-3 text-xs font-medium text-stone-500 uppercase hidden md:table-cell">Views</th>
                <th className="px-4 py-3 text-xs font-medium text-stone-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map(article => (
                <tr key={article.id} className="border-b border-stone-50 hover:bg-stone-25">
                  <td className="px-4 py-3">
                    <div className="font-medium text-sm text-stone-900">{article.title}</div>
                    <div className="text-xs text-stone-400 mt-0.5">
                      {new Date(article.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-500 hidden sm:table-cell">
                    {categories.find(c => c.slug === article.category_slug)?.name || '-'}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      article.status === 'published'
                        ? 'bg-green-50 text-green-700'
                        : article.status === 'scheduled'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-amber-50 text-amber-700'
                    }`}>
                      {article.status}
                    </span>
                    {article.status === 'scheduled' && article.scheduled_at && (
                      <div className="text-xs text-stone-400 mt-0.5">
                        {new Date(article.scheduled_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-500 hidden md:table-cell">
                    {article.views || 0}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => editArticle(article.slug)}
                        className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-700"
                        title="Edit"
                        data-testid={`edit-${article.slug}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <a
                        href={`/app/${tenantSlug}/blog/${article.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-700"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => deleteArticle(article.id)}
                        className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-600"
                        title="Delete"
                        data-testid={`delete-${article.slug}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="category-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
              <h3 className="font-semibold text-stone-900">Manage Categories</h3>
              <button onClick={() => setShowCategoryModal(false)} className="p-1 rounded hover:bg-stone-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Add new */}
              <div className="flex gap-2">
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="New category name"
                  onKeyDown={(e) => e.key === 'Enter' && createCategory()}
                  data-testid="new-category-input"
                />
                <Button onClick={createCategory} size="sm" className="bg-stone-900 hover:bg-stone-800" data-testid="add-category-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* List */}
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {categories.length === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-4">No categories yet</p>
                ) : (
                  categories.map(cat => (
                    <div key={cat.id} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-stone-50">
                      <span className="text-sm text-stone-700">{cat.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-stone-400">{cat.article_count} articles</span>
                        <button
                          onClick={() => deleteCategory(cat.id)}
                          className="p-1 rounded hover:bg-red-50 text-stone-400 hover:text-red-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
